import React, { useState } from 'react';
import { LanguageCode, NewsItem, EventItem, ActivityItem, CultureItem, DocumentItem } from '../types';
import { translations } from '../i18n/translations';
import { getLocalized, getLocalizedCategory } from '../utils/i18n';
import { Search, X, Calendar, FileText, Newspaper, Sparkles, Layers, ChevronRight } from 'lucide-react';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentLang: LanguageCode;
  news?: NewsItem[];
  events?: EventItem[];
  activities?: ActivityItem[];
  cultureItems?: CultureItem[];
  documents?: DocumentItem[];
  onSelectResult?: (type: string, id: string) => void;
  onSelectNews?: (news: NewsItem) => void;
  onSelectEvent?: (event: EventItem) => void;
}

export const SearchModal: React.FC<SearchModalProps> = ({
  isOpen,
  onClose,
  currentLang,
  news = [],
  events = [],
  activities = [],
  cultureItems = [],
  documents = [],
  onSelectResult,
  onSelectNews,
  onSelectEvent,
}) => {
  const [query, setQuery] = useState('');
  if (!isOpen) return null;

  const t = translations[currentLang];
  const q = query.trim().toLowerCase();

  const filteredNews = q
    ? news.filter(
        (item) =>
          getLocalized(item.title, currentLang).toLowerCase().includes(q) ||
          getLocalized(item.summary, currentLang).toLowerCase().includes(q)
      )
    : [];

  const filteredEvents = q
    ? events.filter(
        (item) =>
          getLocalized(item.title, currentLang).toLowerCase().includes(q) ||
          item.location.toLowerCase().includes(q)
      )
    : [];

  const filteredActivities = q
    ? activities.filter((item) => getLocalized(item.title, currentLang).toLowerCase().includes(q))
    : [];

  const filteredCulture = q
    ? cultureItems.filter((item) => getLocalized(item.title, currentLang).toLowerCase().includes(q))
    : [];

  const filteredDocs = q
    ? documents.filter((item) => getLocalized(item.title, currentLang).toLowerCase().includes(q))
    : [];

  const totalResults =
    filteredNews.length +
    filteredEvents.length +
    filteredActivities.length +
    filteredCulture.length +
    filteredDocs.length;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-16 sm:pt-24 px-4 bg-black/80 backdrop-blur-md">
      <div className="w-full max-w-2xl glass-card rounded-3xl p-5 border border-white/10 shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        {/* Search Input Bar */}
        <div className="relative flex items-center mb-4">
          <Search className="absolute left-4 w-5 h-5 text-[#D4AF37]" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t.searchPlaceholder}
            autoFocus
            className="w-full pl-12 pr-10 py-3.5 rounded-2xl bg-[#164336] border border-white/10 text-white placeholder-gray-400 text-sm focus:outline-none focus:border-[#D4AF37]"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="absolute right-4 text-gray-400 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Results Container */}
        <div className="max-h-[60vh] overflow-y-auto space-y-4 pr-1">
          {!q ? (
            <div className="text-center py-8 text-gray-400 text-xs">
              <Search className="w-8 h-8 text-[#D4AF37]/40 mx-auto mb-2" />
              <p>{t.searchPlaceholder}</p>
            </div>
          ) : totalResults === 0 ? (
            <div className="text-center py-8 text-gray-400 text-xs">
              <p>{t.noResults}</p>
            </div>
          ) : (
            <>
              {/* Events Results */}
              {filteredEvents.length > 0 && (
                <div>
                  <p className="text-[11px] font-bold text-[#D4AF37] uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5" />
                    <span>{t.events} ({filteredEvents.length})</span>
                  </p>
                  <div className="space-y-1.5">
                    {filteredEvents.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => {
                          if (onSelectEvent) onSelectEvent(item);
                          if (onSelectResult) onSelectResult('events', item.id);
                          onClose();
                        }}
                        className="w-full text-left p-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/5 flex items-center justify-between text-xs transition-colors"
                      >
                        <div>
                          <p className="font-semibold text-white">{getLocalized(item.title, currentLang)}</p>
                          <p className="text-[10px] text-gray-400 mt-0.5">{item.date} • {item.location}</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-[#D4AF37]" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* News Results */}
              {filteredNews.length > 0 && (
                <div>
                  <p className="text-[11px] font-bold text-[#D4AF37] uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <Newspaper className="w-3.5 h-3.5" />
                    <span>{t.news} ({filteredNews.length})</span>
                  </p>
                  <div className="space-y-1.5">
                    {filteredNews.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => {
                          if (onSelectNews) onSelectNews(item);
                          if (onSelectResult) onSelectResult('news', item.id);
                          onClose();
                        }}
                        className="w-full text-left p-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/5 flex items-center justify-between text-xs transition-colors"
                      >
                        <div>
                          <p className="font-semibold text-white">{getLocalized(item.title, currentLang)}</p>
                          <p className="text-[10px] text-gray-400 mt-0.5">{item.date} • {getLocalizedCategory(item.category, currentLang)}</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-[#D4AF37]" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Activities Results */}
              {filteredActivities.length > 0 && (
                <div>
                  <p className="text-[11px] font-bold text-[#D4AF37] uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <Layers className="w-3.5 h-3.5" />
                    <span>{t.activities} ({filteredActivities.length})</span>
                  </p>
                  <div className="space-y-1.5">
                    {filteredActivities.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => {
                          onSelectResult?.('activities', item.id);
                          onClose();
                        }}
                        className="w-full text-left p-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/5 flex items-center justify-between text-xs transition-colors"
                      >
                        <div>
                          <p className="font-semibold text-white">{getLocalized(item.title, currentLang)}</p>
                          <p className="text-[10px] text-gray-400 mt-0.5">{getLocalizedCategory(item.category, currentLang)}</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-[#D4AF37]" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Culture Results */}
              {filteredCulture.length > 0 && (
                <div>
                  <p className="text-[11px] font-bold text-[#D4AF37] uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5" />
                    <span>{t.culture} ({filteredCulture.length})</span>
                  </p>
                  <div className="space-y-1.5">
                    {filteredCulture.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => {
                          onSelectResult?.('culture', item.id);
                          onClose();
                        }}
                        className="w-full text-left p-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/5 flex items-center justify-between text-xs transition-colors"
                      >
                        <div>
                          <p className="font-semibold text-white">{getLocalized(item.title, currentLang)}</p>
                          <p className="text-[10px] text-gray-400 mt-0.5">{getLocalizedCategory(item.category, currentLang)}</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-[#D4AF37]" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Documents Results */}
              {filteredDocs.length > 0 && (
                <div>
                  <p className="text-[11px] font-bold text-[#D4AF37] uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <FileText className="w-3.5 h-3.5" />
                    <span>{t.documents} ({filteredDocs.length})</span>
                  </p>
                  <div className="space-y-1.5">
                    {filteredDocs.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => {
                          onSelectResult?.('documents', item.id);
                          onClose();
                        }}
                        className="w-full text-left p-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/5 flex items-center justify-between text-xs transition-colors"
                      >
                        <div>
                          <p className="font-semibold text-white">{getLocalized(item.title, currentLang)}</p>
                          <p className="text-[10px] text-gray-400 mt-0.5">{item.fileSize}</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-[#D4AF37]" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="mt-4 pt-3 border-t border-white/10 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-white/10 text-xs font-semibold hover:bg-white/20 transition-colors text-white"
          >
            {t.close}
          </button>
        </div>
      </div>
    </div>
  );
};
